<template>
    <div class="main-container" :style="{ '--logo-col-width': logoColWidth }">
        <div class="header">
            <button
                v-if="selectedMemberIds.length"
                type="button"
                class="header-button selection-action-button"
                @click="handleSelectionAction"
            >
                Update Unavailable Days
            </button>
            <md-checkbox
                class="header-button checkbox"
                :disabled="disableFilters"
                type="checkbox"
                v-model="showUnavailableOnly"
                @change="handleWeekChange"
            >
                Show Unavailable Only
            </md-checkbox>
            <div class="rows-per-page-container">
                <label for="rowsPerPage">Rows per page:</label>
                <select
                    id="rowsPerPage"
                    v-model="selectedRowsPerPage"
                    @change="handleRowsPerPageChange"
                    class="rows-per-page-select"
                    :disabled="disableFilters"
                >
                    <option :value="DEFAULT_ROWS_PER_PAGE">{{ DEFAULT_ROWS_PER_PAGE }}</option>
                    <option :value="null">All</option>
                </select>
            </div>
            <md-datepicker ref="mdDatePicker" v-model="dateFilter" md-immediately :md-debounce="10">
                <label>Date Range</label>
            </md-datepicker>
            <button
                :disabled="disableFilters"
                type="button"
                class="header-button week-nav-button"
                @click="previousWeek()"
            >
                <md-icon class="icon-chevron-right">chevron_left</md-icon>
                <span class="left-button-text">Previous</span>
            </button>
            <button :disabled="disableFilters" type="button" class="header-button week-nav-button" @click="nextWeek()">
                <span class="right-button-text">Next</span>
                <md-icon class="icon-chevron-right">chevron_right</md-icon>
            </button>
        </div>
        <div v-if="!isListLoading">
            <div class="roster-table-wrapper">
                <div class="roster-table-container" @mouseleave="resetHoveredRow()">
                    <table class="roster-table">
                        <thead>
                            <tr>
                                <th class="col-select">
                                    <input
                                        type="checkbox"
                                        :checked="isAllSelected"
                                        :indeterminate.prop="isIndeterminate"
                                        @change="toggleSelectAll"
                                        aria-label="Select all team members"
                                    />
                                </th>
                                <th class="col-photo"></th>
                                <th class="col-name">Team Member</th>
                                <th v-for="(col, colIndex) in dateColumns" :key="`header-${colIndex}`" class="col-date">
                                    {{ col.columnName }}
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr
                                v-for="(item, index) in memberList"
                                :key="index"
                                :class="{ 'hovered-row': hoveredRowIndex === index }"
                                @mouseenter="handleRowHover(index)"
                            >
                                <td class="col-select">
                                    <input
                                        type="checkbox"
                                        :value="item.publicUserId"
                                        v-model="selectedMemberIds"
                                        @click.stop
                                        aria-label="Select team member"
                                    />
                                </td>
                                <td class="col-photo">
                                    <router-link
                                        v-if="loggedInUserEmail != item.email"
                                        :to="{
                                            name: 'Team Member Profile',
                                            params: { publicUserId: item.publicUserId }
                                        }"
                                    >
                                        <img
                                            v-if="item.photoUrl"
                                            class="circle-profile-image"
                                            :src="item.photoUrl"
                                            @error="$_setDefaultBrokenImage"
                                            alt
                                        />
                                        <img v-else class="circle-profile-image" :src="$root.defaultPhotoUrl" alt />
                                    </router-link>
                                    <router-link v-else :to="{ name: 'User Profile' }">
                                        <img
                                            v-if="item.photoUrl"
                                            class="circle-profile-image"
                                            :src="item.photoUrl"
                                            @error="$_setDefaultBrokenImage"
                                            alt
                                        />
                                        <img v-else class="circle-profile-image" :src="$root.defaultPhotoUrl" alt />
                                    </router-link>
                                </td>
                                <td class="col-name">
                                    <router-link
                                        v-if="loggedInUserEmail != item.email"
                                        class="custom-a-blue"
                                        :to="{
                                            name: 'Team Member Profile',
                                            params: { publicUserId: item.publicUserId }
                                        }"
                                    >
                                        {{ item.fullName }}
                                    </router-link>
                                    <router-link v-else class="custom-a-blue" :to="{ name: 'User Profile' }">
                                        {{ item.fullName }}
                                    </router-link>
                                </td>
                                <td
                                    v-for="(col, dateColIndex) in dateColumns"
                                    :key="`cell-${index}-${dateColIndex}`"
                                    class="col-date"
                                >
                                    <div class="indicator-container">
                                        <availability-indicator
                                            class="indicators"
                                            :is-available="isAvailable(item, col)"
                                        />
                                        <span class="info-container" v-if="unavailabilityReason(item, col)">
                                            <md-icon>info</md-icon>
                                            <md-tooltip md-direction="top">
                                                {{ unavailabilityReason(item, col) }}
                                            </md-tooltip>
                                        </span>
                                    </div>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>

            <div v-if="memberList.length == 0 && !isLoading">
                <p class="no-result-message">No results matching your search/filter could be found.</p>
            </div>

            <md-card-actions class="page-footer" md-alignment="space-between">
                <div>
                    <p v-if="total === pagination.perPage" class="card-category">
                        Page {{ pagination.currentPage }} of many
                    </p>
                    <p v-else class="card-category">Page {{ pagination.currentPage }} of {{ totalPages }}</p>
                </div>
                <pagination
                    v-model="pagination.currentPage"
                    class="pagination-no-border pagination-success"
                    :per-page="pagination.perPage"
                    :total="total"
                    @change-page="handleChangePage($event, pagination.perPage)"
                />
            </md-card-actions>
        </div>
        <div v-else>
            <div class="body-list">
                <div class="list-loader">
                    <fade-loader :loading="isListLoading" color="#333333" />
                    <span>LOADING</span>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { Pagination, AvailabilityIndicator } from '@/components';
import { PAGINATION_DEFAULTS } from '@/utils/defaults';
import { GeneralMixin } from '@/mixins/GeneralMixin';
import { mapGetters } from 'vuex';
import FadeLoader from 'vue-spinner/src/FadeLoader';
import { handleRequests } from '@/helpers';
import { DATE_TYPES } from '@/utils/constants';
import moment from 'moment';
import MemberRosterUnavailableDaysModal from '@/pages/TeamMembers/components/MemberRosterUnavailableDaysModal';

const DEFAULT_ROWS_PER_PAGE = 50;

export default {
    name: 'MemberRosterCalendar',
    components: {
        Pagination,
        FadeLoader,
        AvailabilityIndicator
    },
    mixins: [GeneralMixin],
    props: {
        searchText: {
            type: String,
            default: ''
        },
        teamRegionId: {
            type: Number,
            default: null
        },
        memberRole: {
            type: Object,
            default: null
        }
    },
    data() {
        return {
            DEFAULT_ROWS_PER_PAGE,
            pagination: { ...PAGINATION_DEFAULTS, perPage: DEFAULT_ROWS_PER_PAGE },
            memberList: [],
            filters: { isDisabled: false, teamRegionId: null, memberRole: null },
            maxPage: 1,
            isListLoading: false,
            dateColumns: [],
            dateFilter: null,
            appointmentDate: null,
            showUnavailableOnly: false,
            hoveredRowIndex: null,
            referenceDatePageNumber: 1,
            selectedMemberIds: [],
            logoColWidth: '15%',
            selectedRowsPerPage: DEFAULT_ROWS_PER_PAGE
        };
    },
    watch: {
        searchText(newValue) {
            this.handleFilterOrSearch({ searchText: newValue });
        },
        teamRegionId(newValue) {
            const oldTeamRegionId = this.filters.teamRegionId ? this.filters.teamRegionId : null;
            if (newValue !== oldTeamRegionId) {
                this.filters.teamRegionId = newValue;
                this.handleFilterOrSearch({ teamRegionId: newValue });
            }
        },
        memberRole(newValue, oldValue) {
            const newSelectedRole = newValue ? newValue.id : null;
            const oldSelectedRole = oldValue ? oldValue.id : null;
            if (newSelectedRole !== oldSelectedRole) {
                const selectedRoleName = newValue && newSelectedRole ? newValue.name : null;
                this.filters.memberRole = selectedRoleName;
                this.handleFilterOrSearch({ memberRole: selectedRoleName });
            }
        },
        async dateFilter(newValue, oldValue) {
            if (!newValue) {
                this.appointmentDate = null;
                this.referenceDatePageNumber = 1;
                await this.handleWeekChange();
                return;
            }

            const newVal = moment(newValue).format(DATE_TYPES.internationalDate);
            const oldVal = oldValue ? moment(oldValue).format(DATE_TYPES.internationalDate) : null;
            if (newValue === oldValue || newVal === oldVal) 
                return;

            this.appointmentDate = newVal;
            this.referenceDatePageNumber = 1;
            await this.handleWeekChange();
        },
        disableFilters(newValue) {
            this.$nextTick(() => {
                if (this.$refs.mdDatePicker) {
                    if (newValue) 
                        this.$refs.mdDatePicker.$refs.input.$el.setAttribute('disabled', true);
                    else 
                        this.$refs.mdDatePicker.$refs.input.$el.removeAttribute('disabled');
                }
            });
        },
        selectedMemberIds: {
            handler(val) {
                this.$emit('selection-change', val);
            },
            deep: true
        }
    },
    async mounted() {
        this.filters.teamRegionId = this.teamRegionId;

        if (this.memberRole?.id) {
            this.filters.memberRole = this.memberRole.name;
        }

        this.isListLoading = true;
        let currentPage = 1;
        const { type } = this.$route.params;
        if (type === 'team-members') {
            currentPage = Number(this.$route.query.currentPage) || 1;
        }
        const {
            data: { members, totalMembers, columnData }
        } = await this.fetchTeamMemberList(currentPage);

        this.pagination.currentPage = currentPage;
        this.dateColumns = columnData;

        this.assignToList(members, totalMembers || members.length);
        this.isListLoading = false;
    },
    methods: {
        async fetchTeamMemberList(pageNumber = 1) {
            const endpoint = `/api/team-members/roster-calendar`;
            const itemsPerPage = this.selectedRowsPerPage || this.pagination.total || 9999;
            const response = await handleRequests(endpoint, {
                params: {
                    pageNumber,
                    itemsPerPage,
                    referenceDatePageNumber: this.referenceDatePageNumber,
                    referenceDate: this.appointmentDate,
                    showUnavailableOnly: this.showUnavailableOnly,
                    ...this.cleanFilters()
                }
            });
            return response;
        },
        async handleRowsPerPageChange() {
            this.pagination.perPage = this.selectedRowsPerPage || 9999;
            this.pagination.currentPage = 1;

            this.isListLoading = true;
            const {
                data: { members, totalMembers, columnData }
            } = await this.fetchTeamMemberList(1);

            this.dateColumns = columnData;
            this.assignToList(members, totalMembers || members.length);
            this.isListLoading = false;
        },
        assignToList(members, totalMembers) {
            this.memberList = members;
            this.pagination.total = totalMembers;
            const currentIds = members.map((m) => m.publicUserId);
            this.selectedMemberIds = this.selectedMemberIds.filter((id) => currentIds.includes(id));
        },
        isAvailable(item, col) {
            return item.columnData.find((x) => x.columnNumber === col.columnNumber)?.isAvailable ?? false;
        },
        unavailabilityReason(item, col) {
            return item.columnData.find((x) => x.columnNumber === col.columnNumber)?.unavailabityReason;
        },
        cleanFilters() {
            Object.keys(this.filters).forEach((e) => {
                if (!this.filters[e]) 
                    delete this.filters[e];
            });
            return this.filters;
        },
        getMaxPage(page) {
            this.maxPage = page;
        },
        async handleChangePage(currentPage = 1, perPage = 50) {
            this.$_handleLoaderState(true);
            this.pagination.currentPage = currentPage;
            const {
                data: { members, totalMembers }
            } = await this.fetchTeamMemberList(currentPage);
            const membersToAssign = totalMembers || members.length;
            this.assignToList(members, membersToAssign);
            this.$router.replace({ path: this.$route.path, query: { currentPage } });
            this.$_handleLoaderState(false);
        },
        async handleFilterOrSearch(val) {
            this.isListLoading = true;
            this.filters = Object.assign(this.filters, val);
            this.pagination.currentPage = 1;
            const {
                data: { members, totalMembers }
            } = await this.fetchTeamMemberList(1);
            const membersToAssign = totalMembers || members.length;
            this.assignToList(members, membersToAssign);
            this.isListLoading = false;
            this.$emit('on-filter-change', this.filters);
        },
        async nextWeek() {
            if (this.disableFilters) 
                return;

            this.referenceDatePageNumber += 1;

            await this.handleWeekChange();
        },
        async previousWeek() {
            if (this.disableFilters) 
                return;

            this.referenceDatePageNumber -= 1;

            await this.handleWeekChange();
        },
        async handleWeekChange() {
            this.isListLoading = true;
            const {
                data: { members, totalMembers, columnData }
            } = await this.fetchTeamMemberList(1);

            this.dateColumns = columnData;
            this.assignToList(members, totalMembers || members.length);
            this.isListLoading = false;
        },
        handleRowHover(index) {
            this.hoveredRowIndex = index;
        },
        resetHoveredRow() {
            this.hoveredRowIndex = null;
        },
        toggleSelectAll() {
            if (this.isAllSelected) {
                this.selectedMemberIds = [];
            } else {
                this.selectedMemberIds = this.memberList.map((m) => m.publicUserId);
            }
        },
        handleSelectionAction() {
            this.$modal.showWithoutAutoClose(
                MemberRosterUnavailableDaysModal,
                {
                    selectedMemberIds: this.selectedMemberIds,
                    onUpdatedCallback: () => {
                        this.handleWeekChange();
                    }
                },
                {
                    events: {
                        updated: () => this.handleWeekChange()
                    }
                }
            );
        }
    },
    computed: {
        total() {
            return this.pagination.total;
        },
        totalPages() {
            if (this.selectedRowsPerPage === null) {
                return 1; // "All" option - single page
            }
            if (this.total > 0) {
                return Math.ceil(this.total / this.pagination.perPage);
            }
            return 1;
        },
        ...mapGetters({
            isLoading: 'isLoading',
            user: 'user/user',
            teamRegions: 'team/teamRegions'
        }),
        disableFilters() {
            return this.memberList.length === 0 || this.isListLoading;
        },
        loggedInUserEmail() {
            return this.user?.email;
        },
        isAllSelected() {
            return this.memberList.length > 0 && this.selectedMemberIds.length === this.memberList.length;
        },
        isIndeterminate() {
            return this.selectedMemberIds.length > 0 && !this.isAllSelected;
        }
    }
};
</script>

<style lang="scss" scoped>
.custom-tooltip-container {
    display: inline-block;
}

.md-tooltip-top {
    &.toggle-tooltip {
        margin-left: 20px !important;
    }
}

.header {
    margin: 10px 0;
    width: 100%;
    display: flex;
    justify-content: right;
    align-items: center;
    gap: 10px;

    .md-datepicker {
        width: 200px;
    }
}

.header-button {
    background: none;
    border: none;
    float: right;
    cursor: pointer;
}

.checkbox {
    display: flex;
    align-items: center;
    font-size: 14px;
}

.week-nav-button {
    margin-top: 3px;
    font-size: 18px;
    color: #2b93ff;
    cursor: pointer;
}

.week-nav-button:disabled {
    color: #d3d5d7;
    cursor: default;
}

.week-nav-button:disabled i {
    color: #d3d5d7 !important;
    border: 1px solid #d3d5d7;
}

.icon-chevron-right {
    color: #2b93ff !important;
    border: 1px solid #2b93ff;
    border-radius: 12px;
}

.left-button-text {
    padding-left: 10px;
}

.right-button-text {
    padding-right: 10px;
}

.selection-action-button {
    background: #2b93ff;
    color: #fff;
    border-radius: 4px;
    padding: 2px 8px;
    font-size: 12px;
    line-height: 16px;
    display: flex;
    align-items: center;
    height: 24px;
}

.selection-action-button:hover {
    background: #0d7fe9;
}

/* New table styles */
.roster-table-wrapper {
    position: relative;
    width: 100%;
    max-height: calc(100vh - 300px);
    overflow: auto;
    border: 1px solid #e0e0e0;
    border-radius: 4px;
    background: #fff;
}

.roster-table-container {
    position: relative;
    width: 100%;
}

.roster-table {
    width: 100%;
    border-collapse: separate;
    border-spacing: 0;
    font-size: 14px;

    thead {
        position: sticky;
        top: 0;
        z-index: 100;
        background: #fff;

        tr {
            background: #fff;
        }

        th {
            padding: 12px 8px;
            text-align: left;
            font-size: 15px;
            color: rgba(0, 0, 0, 0.87);
            border-bottom: 2px solid #e0e0e0;
            background: #fff;
            white-space: nowrap;
            vertical-align: middle;
            font-weight: unset;
        }
    }

    tbody {
        tr {
            border-bottom: 1px solid #f0f0f0;
            transition: background-color 0.2s;

            &.hovered-row {
                background-color: #fafafa;
            }

            &:hover {
                background-color: #fafafa;
            }
        }

        td {
            padding: 8px;
            vertical-align: middle;
            border-bottom: 1px solid #f0f0f0;

            &.col-photo {
                padding: 4px;
            }
        }
    }

    .col-select {
        width: 50px;
        text-align: center;

        input[type='checkbox'] {
            cursor: pointer;
            width: 16px;
            height: 16px;
            margin: 0;
        }
    }

    .col-photo {
        width: 60px;
        text-align: center;
    }

    .col-name {
        min-width: 180px;
        max-width: 250px;

        .custom-a-blue {
            color: #2b93ff;
            text-decoration: none;

            &:hover {
                text-decoration: underline;
            }
        }
    }

    .col-date {
        width: 110px;
        min-width: 110px;
        text-align: center;
    }
}

.circle-profile-image {
    min-width: 30px !important;
    min-height: 30px !important;
    border-radius: 50% !important;
    object-fit: cover !important;
    display: block !important;
    margin: 0 auto !important;
}

.indicator-container {
    position: relative;
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 24px;

    .indicators {
        width: 20px;
        height: 20px;
    }

    .info-container {
        position: absolute;
        left: 50%;
        transform: translateX(25px);
        cursor: pointer;

        .md-icon {
            font-size: 18px;
            color: #757575;
        }
    }
}

.no-result-message {
    padding: 20px;
    text-align: center;
    color: #757575;
    font-size: 14px;
}

.page-footer {
    margin-top: 20px;
    padding: 16px 0;
}

.rows-per-page-container {
    display: flex;
    align-items: center;
    gap: 8px;

    label {
        font-size: 14px;
        color: rgba(0, 0, 0, 0.87);
        white-space: nowrap;
    }

    .rows-per-page-select {
        padding: 6px 12px;
        border: 1px solid #e0e0e0;
        border-radius: 4px;
        font-size: 14px;
        background-color: #fff;
        cursor: pointer;
        min-width: 80px;

        &:focus {
            outline: none;
            border-color: #2b93ff;
        }

        &:disabled {
            background-color: #f5f5f5;
            cursor: not-allowed;
            color: #999;
        }
    }
}
</style>
